// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { diaHoraCorta, diaIsoAr, hoyAr } from "../shared/fechaAr";
import { PESTANAS, diasAtras } from "../shared/periodoVales";
import { aExcel } from "../services/exportarUrea";
import {
  borrarCargaUrea,
  editarValeUrea,
  traerCargaUrea,
  traerCargasUrea,
  traerCuentaDeVales,
} from "../services/urea";
import { buscarFila, useItemPedido } from "../hooks/useItemPedido";
import {
  TALONARIOS,
  comandoReinicio,
  desdeDonde,
  duplicadosPorTalonario,
  enElTalonario,
  rotuloDe,
  textoOtrosTalonarios,
} from "../shared/talonario";
import FotoPrivada from "../components/funcionales/FotoPrivada";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "./css/urea.css";

/* Cargas de urea: el histórico del informe diario, en el hub.
 *
 * ── MIRAR Y BORRAR, NUNCA CARGAR NI EDITAR ──────────────────────────────────
 *
 * El alta es del bot (foto del vale obligatoria); acá se elige el período
 * —pestañas rápidas o calendario— y se ve lo que el mail de las 09:30 ya
 * cuenta, pero de cualquier día. Con `admin.borrar` (hoy sólo Igna) aparece
 * el tacho: la carga duplicada se borra y listo — no hay editar, porque una
 * fila corregida a mano contradiría a su propia foto. El vale bien leído se
 * consigue re-mandando la foto, no tipeando encima.
 *
 * ── LOS DOS TALONARIOS, EN LA MISMA LISTA Y DISTINGUIBLES ───────────────────
 *
 * Igna, 15/09: *«¿dónde se guardan esos vales en el Hub? ¿Tenemos un sector
 * "Río Grande"? ¿O cómo lo encuentro?»*. Acá, y hasta hoy no se podían
 * encontrar: la columna `serie` viajaba en la consulta y no se mostraba en
 * ningún lado, así que un vale de Río Grande caía mezclado con los de acá sin
 * nada que lo distinguiera.
 *
 * Una pantalla con filtro y no dos pantallas: los vales son una sola lista y
 * el control de la secuencia se hace sobre ella. Ver `shared/talonario`.
 *
 * ── LOS VALES QUE FALTAN SON LOS DEL PARTE ──────────────────────────────────
 *
 * Nico, 11/10: el parte decía «Faltan 86089 vales» por UN vale mal leído
 * (95564) y se agregó el reinicio del conteo («/valesdeurea-reset» en el bot).
 * Esta pantalla contaba otra cosa —los huecos del período que se estaba
 * mirando— y daba otro número para el mismo talonario. Ahora muestra la cuenta
 * del parte: desde el corte (la fecha o el vale del reinicio), con los vales
 * que no siguen la serie aparte, «a revisar», y diciendo desde dónde cuenta.
 * No depende del período ni del filtro: lo que falta es lo que falta.
 *
 * ── LA FOTO SE MIRA ACÁ MISMO ───────────────────────────────────────────────
 *
 * Antes saltaba al panel del bot en otra pestaña, pasando por el SSO. Ahora la
 * abre `FotoPrivada` —el mismo componente de las fotos del puerto— con un link
 * firmado del bucket `vales` y un visor encima de la tabla. Se gana lo obvio
 * (mirar un vale y seguir revisando, sin dos cambios de pestaña por fila) y
 * algo menos obvio: el hub deja de depender de que el panel del bot esté
 * arriba y de que el canje de sesión funcione para poder ver una imagen.
 */

/* El período (Hoy, Ayer, 7 y 30 días) y el día argentino viven en
   `shared/periodoVales`, compartidos con la pantalla de gasoil de la base. */
const hoyAR = () => hoyAr();
const diaAR = (iso) => diaIsoAr(iso);

/* «30/09 14:32», o «30/09 · s/hora» cuando el vale no trae hora: el bot lo
   guarda a las 12:00:00 en punto y mostrarlo así sería inventar una hora
   (Nico, 01/10). Ver `sinHora`. */
const cuando = (iso) => diaHoraCorta(iso);

const litros = (n) =>
  n == null ? "—" : `${new Intl.NumberFormat("es-AR").format(n)} L`;

/* El orden de la tabla. Los numéricos ordenan por número (no por texto, para
 * que 9 no quede después de 89); los vacíos van SIEMPRE al final, mire para
 * donde mire la flecha, porque «sin dato» no es ni el más grande ni el más
 * chico. */
const NUMERICOS = new Set(["interno", "litros", "vale", "km"]);
const valorDeOrden = (c, campo) => {
  if (campo === "fecha") return c.fecha ? new Date(c.fecha).getTime() : null;
  if (campo === "producto") return (c.producto || "").toLowerCase() || null;
  // El talonario ordena por su NOMBRE y no por la columna cruda: `serie` es
  // `null` para el de acá, y un null se iría al fondo como si fuera un dato
  // que falta cuando en realidad es «el de la oficina».
  if (campo === "serie") return rotuloDe(c).toLowerCase();
  if (campo === "cargo")
    return (c.quien_nombre || c.quien || "").toLowerCase() || null;
  if (NUMERICOS.has(campo)) {
    const n = Number(c[campo]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/* ── LA CUENTA DE UN TALONARIO, COMO EL PARTE ──────────────────────────────── */

/* Cuántos números de la lista se escriben antes de resumir. El mail del bot
   lista 40; acá la tabla está abajo y el que necesita el detalle baja el
   Excel de Vales del surtidor. */
const LISTA_MAXIMA = 40;

/* Un talonario sin vales desde el corte y sin reinicio no tiene nada que
   decir (Río Grande, cuando Menchón no cargó). Con reinicio sí: «todavía no
   hay vales desde el 9580» es justo lo que quiere ver el que lo reinició. */
const hayQueDecir = (t) =>
  t.cargados > 0 || t.fuera_de_serie.length > 0 ||
  (t.otros_talonarios || []).length > 0 || !!t.desde_vale;

/* «08/10», del día de carga. */
const diaMes = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");

const listar = (nums) =>
  nums.length <= LISTA_MAXIMA
    ? nums.join(", ")
    : `${nums.slice(0, LISTA_MAXIMA).join(", ")} … y ${nums.length - LISTA_MAXIMA} más`;

function CuentaDeVales({ t }) {
  const n = t.faltan.length;
  const detalle = t.cargados
    ? ` (${t.cargados} de ${t.esperados} cargados, serie ${t.desde}–${t.hasta})`
    : "";
  return (
    <section
      className={"urea-alerta" + (n ? "" : " urea-alerta--bien")}
      aria-label={`Vales que faltan · ${t.rotulo}`}
    >
      {!t.cargados ? (
        <p>
          <b>{t.rotulo}</b> · Todavía no hay vales cargados para controlar.
        </p>
      ) : n ? (
        <p>
          <b>{t.rotulo} · Faltan {n} vale{n > 1 ? "s" : ""}</b>
          {detalle}: {listar(t.faltan)}. Puede que estén sin cargar todavía, o
          salteados.
        </p>
      ) : (
        <p>
          <b>{t.rotulo} · No falta ningún vale</b>{detalle}.
        </p>
      )}
      {/* LOS QUE NO SIGUEN LA SERIE. Un «95564» en un talonario que va por el
          9579 no es el vale siguiente: es un número mal leído. No entra en la
          cuenta —si entrara, inventaría miles de faltantes— y tampoco se
          esconde: se dice cuál es y se abre su carga para mirar la foto.
          Con el mismo tope que los faltantes: con ancla, lo que queda arriba
          de un salto de más de mil queda afuera para siempre, y sin tope la
          línea crecería con cada carga. */}
      {t.fuera_de_serie_filas.length > 0 && (
        <p className="urea-alerta__suave">
          <b>A revisar:</b>{" "}
          {t.fuera_de_serie_filas.slice(0, LISTA_MAXIMA).map((f, i) => (
            <span key={f.numero}>
              {i > 0 && ", "}
              {f.id != null ? <Link to={`?ver=${f.id}`}>{f.vale}</Link> : f.vale}
              {/* El día en que se CARGÓ, no el del papel: el 95564 dice 08/09 y
                  se cargó el 08/10, entre el 9562 y el 9563. */}
              {f.cargado && ` (cargado ${diaMes(f.cargado)})`}
            </span>
          ))}
          {t.fuera_de_serie_filas.length > LISTA_MAXIMA &&
            ` … y ${t.fuera_de_serie_filas.length - LISTA_MAXIMA} más`}
          {" "}— no sigue{t.fuera_de_serie_filas.length > 1 ? "n" : ""} la serie
          de este talonario y no entra{t.fuera_de_serie_filas.length > 1 ? "n" : ""}{" "}
          en la cuenta. Mirá la foto: puede ser un número mal leído.
        </p>
      )}
      {/* OTRO TALONARIO: tres vales seguidos o más no son un número mal leído,
          así que no van «a revisar». Tampoco entran en la cuenta: se dicen, y
          si es el que se está cargando, con el comando del bot para contar
          desde él (el mismo que da el parte). */}
      {(t.otros_talonarios || []).length > 0 && (
        <p className="urea-alerta__suave">
          <b>{t.otros_talonarios.length === 1
            ? "Otro talonario:"
            : `Otros talonarios (${t.otros_talonarios.length}):`}</b>{" "}
          {textoOtrosTalonarios(t.otros_talonarios)} — no entra
          {t.otros_talonarios.length > 1 ? "n" : ""} en la cuenta.
          {comandoReinicio(t) && (
            <>
              {" "}Si arrancó uno nuevo en el {t.reiniciar_desde}, mandale al
              bot <code>{comandoReinicio(t)}</code>.
            </>
          )}
        </p>
      )}
      {t.sin_vale > 0 && (
        <p className="urea-alerta__suave">
          {t.sin_vale === 1
            ? "1 carga sin número de vale queda"
            : `${t.sin_vale} cargas sin número de vale quedan`}{" "}
          afuera de la cuenta.
        </p>
      )}
      {t.anulados_sin_leer && (
        <p className="urea-alerta__suave">
          No pude mirar los vales anulados: puede haber alguno de más en la lista.
        </p>
      )}
      <p
        className="urea-alerta__desde"
        title={t.reinicio_por ? `Reinició ${t.reinicio_por}` : undefined}
      >
        {desdeDonde(t)}
        {t.corte_sin_leer && " (no pude leer si hubo un reinicio)"}.
      </p>
    </section>
  );
}

const Urea = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  /* DOS LLAVES, CUALQUIERA DE LAS DOS. `ver.urea` (18/09) abre esta pantalla
   * sin entregar el gasoil entero: `ver.combustible` abre además Consumo
   * Scania, CCyU y Viajes, así que era imposible darle sólo la urea a alguien.
   * La vieja sigue valiendo, o el que ya la tenía perdía la pantalla. */
  const LLAVES = ["ver.combustible", "ver.urea"];
  const permitido = permisos !== null && LLAVES.some((k) => permisos.includes(k));
  const puedeBorrar = puede("admin.borrar") && permisos !== null;
  /* Editar el número de vale (Nico/Igna, 24/09): sólo tres personas, con
   * registro. La foto sigue siendo la verdad; esto corrige la LECTURA. */
  const puedeEditarVale = puede("editar.vale_urea") && permisos !== null;
  const [editId, setEditId] = useState(null);   // id de la carga en edición
  const [editVal, setEditVal] = useState("");
  const [editando, setEditando] = useState(false);

  const [pestana, setPestana] = useState("7"); // id de pestaña, o "" = calendario
  const [desde, setDesde] = useState(diasAtras(6));
  const [hasta, setHasta] = useState(hoyAR());
  const [datos, setDatos] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [estado, setEstado] = useState(""); // "" | mal:<msg>

  const cargar = () => {
    setDatos(undefined);
    traerCargasUrea(desde, hasta)
      .then(setDatos)
      .catch(() => setError(true));
  };

  /* LA CUENTA DEL PARTE (Nico, 11/10). Se pide aparte del período porque no
     depende de él: mira el talonario desde el corte. `undefined` = cargando,
     `null` = no se pudo, y eso NO se muestra como «no falta ninguno». Se
     vuelve a pedir después de borrar o corregir un vale, que es lo que la
     cambia. */
  const [cuenta, setCuenta] = useState(undefined);
  const contar = () => {
    traerCuentaDeVales()
      .then(setCuenta)
      .catch(() => setCuenta(null));
  };

  useEffect(() => {
    if (!permitido) return;
    contar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido]);

  useEffect(() => {
    if (!permitido) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido, desde, hasta]);

  const elegirPestana = (p) => {
    const [d, h] = p.rango();
    setPestana(p.id);
    setDesde(d);
    setHasta(h);
  };

  /* El talonario que se está mirando. El filtro es de PANTALLA y no de
     consulta: los duplicados se calculan sobre todo lo que trajo el período y
     lo que falta sale de la cuenta del parte, así que cambiar de pestaña no
     puede cambiar ninguna de las dos cosas. */
  const [talonario, setTalonario] = useState("todos");

  const visibles = useMemo(
    () => enElTalonario(datos || [], talonario), [datos, talonario]);

  const totalLitros = useMemo(
    () => visibles.reduce((s, c) => s + (Number(c.litros) || 0), 0),
    [visibles],
  );

  // El orden elegido. Arranca por fecha, lo más nuevo arriba (como venía).
  const [orden, setOrden] = useState({ campo: "fecha", dir: "desc" });
  const clickOrden = (campo) =>
    setOrden((o) =>
      o.campo === campo
        ? { campo, dir: o.dir === "asc" ? "desc" : "asc" }
        : { campo, dir: campo === "fecha" ? "desc" : "asc" },
    );

  const datosOrdenados = useMemo(() => {
    const arr = [...visibles];
    const { campo, dir } = orden;
    const s = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = valorDeOrden(a, campo);
      const vb = valorDeOrden(b, campo);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // los vacíos, siempre al final
      if (vb == null) return -1;
      if (va < vb) return -s;
      if (va > vb) return s;
      return 0;
    });
    return arr;
  }, [visibles, orden]);

  // Vales cargados dos veces en el período que se está mirando. Igna, 04/09:
  // cargaron el 9265 como si fuera el 9266 → el 9265 quedó dos veces. Los que
  // FALTAN ya no salen de acá sino de la cuenta del parte (`cuenta`, arriba):
  // el hueco del período era otro número que el del bot.
  //
  // SE CUENTA POR TALONARIO: el mismo número en dos talonarios son dos papeles
  // distintos. Y SOBRE `datos`, NO SOBRE `visibles`: no depende de qué pestaña
  // esté mirando el que abre la pantalla.
  const duplicados = useMemo(() => duplicadosPorTalonario(datos || []), [datos]);

  /* LA CARGA QUE PIDIÓ EL BUSCADOR. Igna, 02/10: «que se abra en el ítem
   * exacto, no solo la pantalla». El buscador encuentra vales de cualquier
   * fecha y la pantalla arranca en «7 días»: si la carga es de antes, la fila
   * no estaba y el link llevaba a una tabla donde lo buscado no aparece.
   *
   * Primero se mira lo que ya trajo el período (lo normal: un vale de esta
   * semana, cero consultas de más). Si no está, se pide esa carga sola por id
   * y el calendario se lleva a SU día; al llegar ese día, la fila titila. El
   * talonario también se abre a «Todos» si la escondía. Si la base no la
   * devuelve —se borró, o no se puede ver— se dice en vez de dejar al que
   * llega buscando en una tabla que no la tiene. */
  const { ver, resaltar, listo } = useItemPedido();
  const [avisoPedido, setAvisoPedido] = useState("");
  const buscado = useRef("");

  useEffect(() => {
    if (!ver) buscado.current = "";
    if (!ver || datos === undefined) return;
    const fila = datos.find((c) => String(c.id) === ver);
    if (fila) {
      if (enElTalonario([fila], talonario).length === 0) {
        setTalonario("todos");
        return; // el próximo dibujo ya la tiene en pantalla
      }
      setAvisoPedido("");
      resaltar(buscarFila(ver));
      listo();
      return;
    }
    if (buscado.current === ver) {
      // Ya se fue a su día y tampoco está: no hay más dónde buscarla.
      setAvisoPedido("No encontré esa carga de urea.");
      listo();
      return;
    }
    buscado.current = ver;
    const noEsta = () => {
      setAvisoPedido("No encontré esa carga de urea.");
      listo();
    };
    traerCargaUrea(ver)
      .then((c) => {
        if (!c?.fecha) return noEsta();
        const dia = diaAR(c.fecha);
        // Ya se estaba mirando ese día y no vino: no está a la vista.
        if (dia >= desde && dia <= hasta) return noEsta();
        setPestana("");
        setDesde(dia);
        setHasta(dia);
      })
      .catch(noEsta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ver, datos, talonario]);

  const thOrden = (campo, label, num = false) => {
    const activa = orden.campo === campo;
    return (
      <th
        className={
          "urea-th-orden" +
          (activa ? " activa" : "") +
          (num ? " num" : "")
        }
        onClick={() => clickOrden(campo)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            clickOrden(campo);
          }
        }}
        tabIndex={0}
        role="columnheader"
        aria-sort={
          activa ? (orden.dir === "asc" ? "ascending" : "descending") : "none"
        }
        title="Ordenar por esta columna"
      >
        {label}
        <span className="urea-flecha" aria-hidden="true">
          {activa ? (orden.dir === "asc" ? " ▲" : " ▼") : ""}
        </span>
      </th>
    );
  };

  const borrar = async (c) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `¿Borrar la carga del interno ${c.interno || "?"} (${litros(c.litros)}` +
        `${c.vale ? `, vale ${c.vale}` : ", sin vale"})? No hay papelera: ` +
        "si hacía falta, se vuelve a mandar la foto del vale al bot.",
    );
    if (!ok) return;
    try {
      await borrarCargaUrea(c.id);
      setEstado("");
      cargar();
      contar();
    } catch (e) {
      setEstado(`mal:${e?.message || "No se pudo borrar."}`);
    }
  };

  const abrirEdicion = (c) => { setEditId(c.id); setEditVal(c.vale || ""); };
  const cancelarEdicion = () => { setEditId(null); setEditVal(""); };
  const guardarVale = async (c) => {
    setEditando(true);
    try {
      await editarValeUrea(c.id, editVal);
      setEstado("");
      cancelarEdicion();
      cargar();
      contar();
    } catch (e) {
      setEstado(`mal:${e?.message || "No se pudo editar el vale."}`);
    } finally {
      setEditando(false);
    }
  };

  if (permisos !== null && !permitido) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      <div className="urea-barra">
        <div className="urea-periodos" role="group" aria-label="Período">
          {PESTANAS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={"urea-seg" + (pestana === p.id ? " activo" : "")}
              onClick={() => elegirPestana(p)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="urea-rango">
          <input
            type="date"
            value={desde}
            max={hasta}
            onChange={(e) => {
              setPestana("");
              setDesde(e.target.value);
            }}
            aria-label="Cargas desde esta fecha"
          />
          <span className="urea-rango-sep">→</span>
          <input
            type="date"
            value={hasta}
            min={desde}
            onChange={(e) => {
              setPestana("");
              setHasta(e.target.value);
            }}
            aria-label="Cargas hasta esta fecha"
          />
        </div>
        {/* EL TALONARIO. Va al lado del período porque es la otra pregunta
            que se hace el que abre esto: de cuándo, y de cuál. */}
        <div className="urea-periodos" role="group" aria-label="Talonario">
          {TALONARIOS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={"urea-seg" + (talonario === t.id ? " activo" : "")}
              onClick={() => setTalonario(t.id)}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
        {/* BAJAR LO QUE ESTÁ EN PANTALLA. Nico, 16/09: «Necesito poder
            exportar a excel los datos de urea». Sale el período elegido, el
            talonario elegido y el orden elegido: lo que se ve.

            El botón aparece sólo con filas. Uno que baja un archivo vacío no
            informa que no hay nada —eso ya lo dice el cartel de al lado—, hace
            abrir Excel para enterarse. */}
        {datos !== undefined && visibles.length > 0 && (
          <button
            type="button"
            className="urea-seg"
            onClick={() => aExcel(datosOrdenados, desde, hasta)}
          >
            Bajar Excel
          </button>
        )}
        {datos !== undefined && visibles.length > 0 && (
          <div className="urea-resumen">
            <span className="urea-cifra">{visibles.length}</span>
            <span className="urea-unidad">
              {visibles.length === 1 ? "carga" : "cargas"}
            </span>
            <span className="urea-cifra">
              {new Intl.NumberFormat("es-AR", {
                maximumFractionDigits: 1,
              }).format(totalLitros)}
            </span>
            <span className="urea-unidad">litros</span>
          </div>
        )}
        {estado.startsWith("mal:") && (
          <span className="pill mal">{estado.slice(4)}</span>
        )}
      </div>

      {/* Dónde se carga y se corrige, porque acá no. Jorge (29/09) bajó el Excel
          de esta pantalla, lo corrigió y no encontraba cómo subirlo: la
          corrección vive en «Vales del surtidor» (el Parte), y encima el Excel
          que se corrige es el de ALLÁ, que trae la columna «Ref». Un cartel que
          lo diga acá evita esa vuelta —y que el Excel de acá termine en el
          corrector, donde no engancha. */}
      <p className="urea-ayuda-corregir">
        Esta pantalla es para <b>mirar</b> las cargas y bajar el Excel. Para{" "}
        <b>cargar vales a mano</b> o <b>corregir</b> los que están mal, andá a{" "}
        <Link to="/panel/vales">Vales del surtidor</Link> — ahí están{" "}
        «cargar vales a mano» y «corregir desde Excel». El Excel que se corrige
        es el que bajás <b>desde ahí</b> (trae la columna «Ref»), no el de esta
        pantalla.
      </p>

      {avisoPedido && (
        <div className="panel-aviso suave" role="status">
          <h3>{avisoPedido}</h3>
          <p className="panel-aviso__chico">
            Puede que la hayan borrado. Las demás cargas siguen acá abajo.
          </p>
        </div>
      )}

      {datos !== undefined && visibles.length === 0 && (
        <div className="panel-aviso suave">
          <h3>
            {datos.length === 0
              ? "Sin cargas en el período"
              : "Sin cargas de ese talonario en el período"}
          </h3>
          {datos.length > 0 && (
            <p className="panel-aviso__chico">
              {`Hay ${datos.length} en el período, de otro talonario.`}
            </p>
          )}
        </div>
      )}

      {duplicados.map((a) => (
        <div className="urea-alerta" key={`dup-${a.id}`}>
          {/* EL TALONARIO SE NOMBRA SIEMPRE, incluso con uno solo en pantalla:
              un aviso sin decir de cuál manda a buscar el papel en la oficina
              equivocada. */}
          <p>
            <b>
              ⚠️ {a.rotulo} · Vale
              {a.duplicados.length > 1 ? "s" : ""} cargado
              {a.duplicados.length > 1 ? "s" : ""} más de una vez:
            </b>{" "}
            {a.duplicados.join(", ")}. Fijate si uno es en realidad
            otro número (por ejemplo, uno cargado como el que falta).
          </p>
        </div>
      ))}

      {cuenta === null && (
        <p className="urea-alerta__suave" role="status">
          No pude contar los vales que faltan. Probá recargar la página.
        </p>
      )}
      {(cuenta || []).filter(hayQueDecir).map((t) => (
        <CuentaDeVales key={`cuenta-${t.id}`} t={t} />
      ))}

      {datos !== undefined && visibles.length > 0 && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                {thOrden("fecha", "Cuándo")}
                {thOrden("interno", "Interno")}
                {thOrden("litros", "Litros", true)}
                {thOrden("vale", "Vale", true)}
                {thOrden("km", "Odómetro", true)}
                {thOrden("producto", "Producto")}
                {thOrden("serie", "Talonario")}
                {thOrden("cargo", "Cargó")}
                <th>Vale (foto)</th>
                {puedeBorrar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {datosOrdenados.map((c) => (
                <tr key={c.id} data-ver={c.id}>
                  <td>{cuando(c.fecha)}</td>
                  <td>{c.interno || <span className="tenue">—</span>}</td>
                  <td>{litros(c.litros)}</td>
                  {/* EL VALE ES UNO SOLO. Acá se mostraba también un
                      «(a mano: …)» tomado de `vale_manual`, pensando que el
                      papel traía dos números de talonario. No: el manuscrito
                      de arriba a la derecha es el ODÓMETRO (Igna, 21/08/2026),
                      y esta celda lo habría mostrado como un vale. Nunca se
                      disparó porque el campo quedó siempre vacío — el error
                      estaba esperando la primera foto que lo leyera bien. El
                      kilometraje va en su propia columna. */}
                  {/* Editar el número (Nico/Igna, 24/09): sólo con
                      editar.vale_urea. La foto NO se toca; esto arregla la
                      LECTURA cuando el bot leyó mal y el papel está bien. Queda
                      registro de quién y cuándo (vale_editado_por). */}
                  <td>
                    {editId === c.id ? (
                      <span className="urea-editvale">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarVale(c);
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                          aria-label="Número de vale"
                          autoFocus
                          style={{ width: 84 }}
                        />
                        <button type="button" className="tk-boton tk-boton--chico"
                          disabled={editando} onClick={() => guardarVale(c)}>✓</button>
                        <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                          disabled={editando} onClick={cancelarEdicion}>✕</button>
                      </span>
                    ) : (
                      <>
                        {c.vale || <span className="tenue">sin vale</span>}
                        {puedeEditarVale && (
                          <button
                            type="button"
                            className="urea-lapiz"
                            title={c.vale_editado_por
                              ? `Editado por ${c.vale_editado_por}`
                              : "Editar el número de vale"}
                            aria-label="Editar el número de vale"
                            onClick={() => abrirEdicion(c)}
                          >✎</button>
                        )}
                        {c.vale_editado_por && (
                          <span className="tenue" title={`Editado por ${c.vale_editado_por}`}> ·editado</span>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {c.km != null
                      ? `${new Intl.NumberFormat("es-AR").format(c.km)} km`
                      : <span className="tenue">—</span>}
                  </td>
                  <td>{c.producto || <span className="tenue">—</span>}</td>
                  <td>{rotuloDe(c)}</td>
                  <td>{c.quien_nombre || c.quien || <span className="tenue">—</span>}</td>
                  <td>
                    {c.foto ? (
                      <FotoPrivada
                        ruta={c.foto}
                        bucket="vales"
                        etiqueta="Ver foto"
                        titulo={`Vale ${c.vale || "s/n"} · interno ${c.interno || "?"}`}
                      />
                    ) : (
                      <span className="tenue">sin foto</span>
                    )}
                  </td>
                  {puedeBorrar && (
                    <td className="agenda-acc">
                      <button
                        type="button"
                        className="tk-boton tk-boton--chico tk-boton--flojo"
                        onClick={() => borrar(c)}
                      >
                        Borrar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Urea;
